import {UIComponent} from "@webfruits/core";
import {CoreServices} from "../../core/CoreServices";
import {TextField} from "@webfruits/toolbox/dist/components/text/TextField";
import {Font} from "../../style/Font";
import {Size} from "../../style/Size";
import {Color} from "../../style/Color";
import {gsap} from "gsap";
import {AppConfig} from "../../config/AppConfig";
import {QueryStylesController} from "@webfruits/toolbox/dist/controller/style/QueryStylesController";
import {SVGComponent} from "@webfruits/toolbox/dist/components/svg/SVGComponent";
import {CoreAssets} from "../../core/CoreAssets";
import {ScrollBox} from "@webfruits/toolbox/dist/components/scrollbox/ScrollBox";

/******************************************************************
 * CopySystem
 *
 * @author matthias.schulz@jash.de
 *****************************************************************/

export class CopySystem extends UIComponent {

    /******************************************************************
     * Properties
     *****************************************************************/

    private _titleField: TextField;
    private _descField: TextField;
    private _additionalDescFieldStyles: QueryStylesController;
    private _scrollBox: ScrollBox;

    /******************************************************************
     * Constructor
     *****************************************************************/

    constructor(private _core: CoreServices) {
        super("copy-system");
        this.initScrollBox();
        this.initTitleField();
        this.initDescField();
        this.hideCopy();
        this._core.state.SELECTED_HOTSPOT.onChangeSignal.add(() => this.onSelectedHotspotChanged());
    }

    /******************************************************************
     * Public Methodes
     *****************************************************************/

    public updateStyles() {
        this.applyStyle({
            position: "absolute",
            userSelect: "none"
        })
        this._scrollBox.applyStyle({
            position: "absolute",
            left: Size.APP_WIDTH * 0.1,
            width: Size.APP_WIDTH * 0.8,
            height: Size.APP_HEIGHT
        })
        this._titleField.applyStyle({
            marginTop: Size.APP_HEIGHT * 0.5,
            textAlign: "center",
            color: Color.BLACK,
        })
        this._descField.applyStyle({
            marginTop: "1.5em",
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "center",
            maxWidth: 700,
            color: Color.WHITE
        })
        this._additionalDescFieldStyles.to("strong", {
            ...Font.P_STRONG_FONTSTYLE
        })
        this._additionalDescFieldStyles.to("span.arrow", {
            display: "inline-block",
            width: 18,
            position: "relative",
            marginRight: "0.5em"
        })
    }

    /******************************************************************
     * Private Methodes
     *****************************************************************/

    private initScrollBox() {
        this._scrollBox = new ScrollBox({
            direction: "y",
            hideScrollBar: true
        })
        this._scrollBox.onScrollSignal.add(() => this.onScrolled());
        this._scrollBox.enableScrolling = true;
        this.addChild(this._scrollBox);
    }

    private initTitleField() {
        this._titleField = new TextField({
            fontStyle: () => Font.H1_FONTSTYLE,
            name: "title-field"
        });
        this._scrollBox.addChild(this._titleField);
    }

    private initDescField() {
        this._descField = new TextField({
            fontStyle: () => Font.P_FONTSTYLE,
            name: "desc-field"
        });
        this._additionalDescFieldStyles = new QueryStylesController(this._descField.view);
        this._scrollBox.addChild(this._descField);
    }

    private showCopy() {
        this.interactive = true;
        this._titleField.html = this._core.state.SELECTED_HOTSPOT.getValue().title;
        this._descField.html = this._core.state.SELECTED_HOTSPOT.getValue().desc;
        const arrowFrame = this._descField.view.querySelectorAll(".arrow")[0];
        if (arrowFrame) {
            const descArrow = new SVGComponent(CoreAssets.DESC_ARROW);
            descArrow.clearSizeDefinition();
            arrowFrame.appendChild(descArrow.view);
        }
        this.updateStyles();
        gsap.killTweensOf(this._titleField.view);
        gsap.set(this._titleField.view, {
            y: 50,
            alpha: 0
        })
        gsap.to(this._titleField.view, {
            duration: 0.8,
            delay: AppConfig.HOTSPOT_SELECT_DURATION * 0.1,
            y: 0,
            alpha: 1,
            ease: "power4.out"
        })
        gsap.killTweensOf(this._descField.view);
        gsap.set(this._descField.view, {
            y: 50,
            alpha: 0
        })
        gsap.to(this._descField.view, {
            duration: 0.8,
            delay: AppConfig.HOTSPOT_SELECT_DURATION * 0.2,
            startAt: {
                y: 50,
                alpha: 0
            },
            y: 0,
            alpha: 1,
            ease: "power4.out"
        })
    }

    private hideCopy() {
        this.interactive = false;
        gsap.to(this._titleField.view, {
            duration: 0.2,
            y: 10,
            alpha: 0,
            ease: "power4.out",
            onComplete: () => {
                this._titleField.html = "";
            }
        })
        gsap.to(this._descField.view, {
            duration: 0.2,
            y: 10,
            alpha: 0,
            ease: "power4.out",
            onComplete: () => {
                this._descField.html = "";
            }
        })
    }

    /******************************************************************
     * Events
     *****************************************************************/

    private onSelectedHotspotChanged() {
        if (this._core.state.SELECTED_HOTSPOT.hasValue()) {
            this.showCopy();
        } else {
            this.hideCopy();
        }
    }

    private onScrolled() {
        this._core.state.COPY_SCROLL_TOP.setValue(this._scrollBox.scrollTop);
    }
}
